<template>
  <div>
    <div id="statusbox-notifications"></div>
    <component :is="layout.component" v-bind="layout.props">
      <DevEnvironmentBar />
      <div :class="$style.centered" v-if="ErrorPage">
        <ErrorPage />
      </div>
      <RouterView v-else v-slot="{ Component }">
        <Suspense :timeout="0">
          <component :is="Component" />
          <template #fallback>
            <div :class="$style.centered">
              <LoadingIndicator />
            </div>
          </template>
        </Suspense>
      </RouterView>
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed, onErrorCaptured, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DevEnvironmentBar from '@/components/organisms/DevEnvironmentBar/DevEnvironmentBar.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'

import type { Component, DefineComponent } from 'vue'
import { useLoggedInPlanner } from './hooks/useLoggedInPlanner'
import { useApolloClient } from '@vue/apollo-composable'

import {
  ErrorBadRequest,
  ErrorPageForbidden,
  ErrorPageInternalServerError,
  ErrorPageNotFound,
} from '@/pages/ErrorPage'
import { APIError } from './libs/utilities/CustomErrors'
import { useHead } from '@unhead/vue'

const error = ref<Error | null>(null)
onErrorCaptured(err => {
  error.value = err
})
const router = useRouter()
router.beforeResolve((_to, _from, next) => {
  error.value = null
  next()
  return
})
const ErrorPage = computed(() => {
  if (error.value == null) {
    return null
  }
  if (!(error.value instanceof APIError)) {
    return ErrorPageInternalServerError
  }
  switch (error.value?.status) {
    case undefined: {
      return null
    }
    case 400: {
      return ErrorBadRequest
    }
    case 404: {
      return ErrorPageNotFound
    }
    case 403: {
      return ErrorPageForbidden
    }
    case 500: {
      return ErrorPageInternalServerError
    }
    default: {
      return ErrorPageInternalServerError
    }
  }
})

const route = useRoute()
const layout = computed<{
  component: Component | undefined
  props?: Record<string, unknown>
}>(() => {
  const componentLayout = (
    route.matched[route.matched.length - 1].components
      ?.default as DefineComponent
  ).layout

  const layout = componentLayout ?? route.meta.layout

  return {
    component: layout?.component,
    props: layout?.props ?? {},
  }
})

useHead({
  titleTemplate: (title?: string) =>
    title ? `${title} - オカネコBiz` : 'オカネコBiz',
  script: [
    {
      src: import.meta.env.VITE_STATUSBOX_JS,
      type: 'module',
      defer: true,
    },
  ],
})

const apollo = useApolloClient()

const { onLoggedOut } = useLoggedInPlanner()

onLoggedOut(async () => {
  await apollo.client.clearStore()
})

window.StatusBox = window['StatusBox'] || []
// eslint-disable-next-line fp/no-mutating-methods
window.StatusBox?.push(import.meta.env.VITE_STATUSBOX_JSON)
</script>

<style lang="scss" module>
.centered {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
</style>
